// src/components/AdminPortal/PayslipView.jsx
import React, { useRef } from "react";
import html2pdf from "html2pdf.js";

const PayslipView = ({ payslip, allowDownload = true }) => {
  const templateRef = useRef();

  if (!payslip) return <p className="text-gray-500">Loading payslip...</p>;

  const handleDownloadPDF = () => {
    if (!templateRef.current) return;

    const opt = {
      margin: 0,
      filename: `${payslip.payslipNo || "payslip"}.pdf`,
      image: { type: "jpeg", quality: 1 },
      html2canvas: { scale: 3, useCORS: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };

    html2pdf().set(opt).from(templateRef.current).save();
  };

  const tableClass = "w-full border-collapse table-fixed text-xs";
  const cellClass = "px-1 py-1 border border-gray-300 align-top leading-tight text-xs";
  const tableWrapperClass = "border border-gray-400 rounded-lg overflow-hidden mb-4";

  return (
    <div className="max-w-full mx-auto mt-6 text-xs px-2">
      {/* Download Button */}
      <div className="flex justify-end gap-2 mb-3">
        {allowDownload && (
          <button
            onClick={handleDownloadPDF}
            className="px-4 py-1 bg-yellow-400 text-black font-semibold rounded"
          >
            Download PDF
          </button>
        )}
      </div>

      <div
        ref={templateRef}
        className="bg-white text-black p-4"
        style={{
          width: "210mm",
          maxHeight: "297mm",
          padding: "10mm",
          boxSizing: "border-box",
        }}
      >
        {/* Header */}
        <div className="text-center mb-2">
          <h2 className="text-2xl font-bold">LONO</h2>
        </div>
        
        <div className="flex items-start justify-between mb-3">
          {/* Left Info */}
          <div className="text-left">
            <p className="font-semibold">BUSICON SERVICES PRIVATE LIMITED</p>
            <p className="text-xs">CIN: U70200TS2023PTC178463</p>
            <p className="text-xs">
              17-3-90/2/1A, Beside V Mart, Block Office X Road, Siddipet, Telangana, 502103
            </p>
            <p className="text-xs">Phone: 7658934523 | support@lono.org.in</p>
          </div>
          {/* Logo Right */}
          <div>
            <img
              src="/assets/lono_logo.jpg"
              alt="LONO Logo"
              className="w-24 h-24 rounded-full object-cover border-2 border-gray-300"
            />
          </div>
        </div>

        {/* Payslip Title */}
        <h3 className="font-bold text-left mb-2">
          PAYSLIP for {payslip.month} {payslip.year}
        </h3>

        {/* Payslip Info */}
        <p className=" mb-2">Payslip No.: {payslip.payslipNo}</p>
         

        {/* Employee Details */}
        <h4 className="font-semibold mb-2 mt-4">Employee Details</h4>
        <table className={tableClass}>
          <tbody>
            {[
              ["Employee Name", payslip.employeeName],
              ["Employee ID", payslip.employeeId],
              ["Designation", payslip.designation],
              ["Department", payslip.department],
              ["Date of Joining", payslip.doj],
              ["Date of Birth", payslip.dob],
            ].map(([label, value], i) => (
              <tr key={i}>
                <td className={`${cellClass} font-semibold`}>{label}</td>
                <td className={cellClass}>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Banking Details */}
        <h4 className="font-semibold mb-2 mt-4">Banking Details</h4>
        <table className={tableClass}>
          <tbody>
            {[
              ["Bank A/C", payslip.bankAccount],
              ["IFSC", payslip.ifsc],
              ["PAN", payslip.pan],
              ["UAN", payslip.uan],
              ["Days Worked", payslip.daysWorked],
            ].map(([label, value], i) => (
              <tr key={i}>
                <td className={`${cellClass} font-semibold`}>{label}</td>
                <td className={cellClass}>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Earnings */}
        <h4 className="font-semibold mb-2 mt-4">Earnings</h4>
        <table className={tableClass}>
          <tbody>
            {[
              ["Basic Pay", payslip.earnings?.basicPay],
              ["House Rent Allowance", payslip.earnings?.hra],
              ["Special Allowance", payslip.earnings?.specialAllowance],
              ["Performance Incentive", payslip.earnings?.incentive],
              ["Total Earnings", payslip.earnings?.totalEarnings],
            ].map(([label, value], i) => (
              <tr key={i} className={label === "Total Earnings" ? "font-bold" : ""}>
                <td className={cellClass}>{label}</td>
                <td className={cellClass}>₹{value}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Deductions */}
        <h4 className="font-semibold mb-2 mt-4">Deductions</h4>
        <table className={tableClass}>
          <tbody>
            {[
              ["Provident Fund (PF)", payslip.deductions?.pf],
              ["Professional Tax", payslip.deductions?.professionalTax],
              ["Income Tax (TDS)", payslip.deductions?.incomeTax],
              ["Other Deductions", payslip.deductions?.other],
              ["Total Deductions", payslip.deductions?.totalDeductions],
            ].map(([label, value], i) => (
              <tr key={i} className={label === "Total Deductions" ? "font-bold" : ""}>
                <td className={cellClass}>{label}</td>
                <td className={cellClass}>₹{value}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Net Salary */}
        <h4 className="font-semibold mb-2 mt-4">Net Pay</h4>
        <table className={tableClass}>
          <tbody>
            {[
              ["Net Salary (Figures)", payslip.netSalary?.figures],
              ["Net Salary (Words)", payslip.netSalary?.words],
            ].map(([label, value], i) => (
              <tr key={i}>
                <td className={`${cellClass} font-semibold`}>{label}</td>
                <td className={cellClass}>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Disclaimer */}
        <p className="text-xs italic text-gray-500 mt-6 ml-6">
          *This is a computer-generated payslip and does not require a physical signature.
        </p>


      </div>
    </div>
  );
};

export default PayslipView;
